import {
  markQuestionErrors,
  trimFormInputs,
  validateForm,
} from '../components/Header/helpers/formSubmitHelpers';
import type { LanguageOption } from 'src/utils/languages';
import {
  applyFormPublishTranslationIssuesToQuestions,
  clearFormPublishTranslationUiErrors,
  collectFormPublishTranslationIssues,
  type FormPublishTranslationIssue,
} from './formPublishTranslationValidation';
import { DEFAULT_FORM_EDITING_LOCALE } from './localizedQuestionFieldHelpers';
import {
  FormAction,
  FORM_ACTIONS,
  QuestionData,
  CountryOption,
} from '../types';

interface ValidateFormInputs {
  isSpeakUpForm: boolean;
  title: string;
  description: string;
  categoryName: string;
  categoryDescription: string;
  questions: QuestionData[];
  selectedCountryValue: string | undefined;
  selectedCountries?: CountryOption[];
  action: FormAction;
  setTitle: (value: string) => void;
  setDescription: (value: string) => void;
  setCategoryName: (value: string) => void;
  setCategoryDescription: (value: string) => void;
  setQuestions: (questions: QuestionData[]) => void;
  setTitleTouched: (value: boolean) => void;
  setDescriptionTouched: (value: boolean) => void;
  setCategoryNameTouched: (value: boolean) => void;
  setCategoryDescriptionTouched: (value: boolean) => void;
  setCountryTouched: (value: boolean) => void;
  titleTouched: boolean;
  descriptionTouched: boolean;
  categoryNameTouched: boolean;
  categoryDescriptionTouched: boolean;
  countryTouched: boolean;
  selectedLanguages?: LanguageOption[];
}

export interface ValidationResult {
  isValid: boolean;
  trimmedTitle: string;
  trimmedDescription: string;
  trimmedCategoryName: string;
  trimmedCategoryDescription: string;
  trimmedQuestions: QuestionData[];
  titleError: string | null;
  descriptionError: string | null;
  categoryNameError: string | null;
  categoryDescriptionError: string | null;
  countryError: boolean;
  hasQuestionErrors: boolean;
  questionsWithInvalidText: QuestionData[];
  questionsWithInvalidDescription: QuestionData[];
  questionsWithoutAnswerType: QuestionData[];
  questionsWithEmptyOptions: QuestionData[];
  questionsWithInvalidOptions: QuestionData[];
  questionsWithMissingDatabaseField: QuestionData[];
  formPublishTranslationIssues: FormPublishTranslationIssue[];
  hasFormPublishTranslationErrors: boolean;
}

export function validateAndTrimForm(
  inputs: ValidateFormInputs,
): ValidationResult {
  const {
    isSpeakUpForm,
    title,
    description,
    categoryName,
    categoryDescription,
    questions,
    selectedCountryValue,
    selectedCountries,
    action,
    setTitle,
    setDescription,
    setCategoryName,
    setCategoryDescription,
    setQuestions,
    setTitleTouched,
    setDescriptionTouched,
    setCategoryNameTouched,
    setCategoryDescriptionTouched,
    setCountryTouched,
    titleTouched,
    descriptionTouched,
    categoryNameTouched,
    categoryDescriptionTouched,
    countryTouched,
    selectedLanguages: selectedLanguagesInput,
  } = inputs;

  const selectedLanguages = selectedLanguagesInput ?? [];

  const clearPass =
    isSpeakUpForm && action !== FORM_ACTIONS.PREVIEW
      ? clearFormPublishTranslationUiErrors(questions)
      : { next: questions, changed: false };

  const {
    trimmedTitle,
    titleChanged,
    trimmedDescription,
    descriptionChanged,
    trimmedCategoryName,
    categoryNameChanged,
    trimmedCategoryDescription,
    categoryDescriptionChanged,
    trimmedQuestions: trimmedFromInputs,
    questionsChanged,
  } = trimFormInputs(
    title,
    description,
    categoryName,
    categoryDescription,
    clearPass.next,
  );

  const trimmedQuestions = trimmedFromInputs;

  if (titleChanged) setTitle(trimmedTitle);
  if (descriptionChanged) setDescription(trimmedDescription);
  if (categoryNameChanged) setCategoryName(trimmedCategoryName);
  if (categoryDescriptionChanged)
    setCategoryDescription(trimmedCategoryDescription);
  if (questionsChanged || clearPass.changed) {
    setQuestions(trimmedQuestions);
  }

  const validation = validateForm(
    action,
    isSpeakUpForm,
    trimmedTitle,
    trimmedDescription,
    trimmedCategoryName,
    trimmedCategoryDescription,
    selectedCountries && selectedCountries.length > 0
      ? selectedCountries[0].value
      : selectedCountryValue,
    trimmedQuestions,
  );

  const {
    titleError,
    descriptionError,
    categoryNameError,
    categoryDescriptionError,
    countryError,
    hasQuestionErrors,
    questionsWithInvalidText,
    questionsWithInvalidDescription,
    questionsWithoutAnswerType,
    questionsWithEmptyOptions,
    questionsWithInvalidOptions,
    questionsWithMissingDatabaseField,
  } = validation;

  const hasErrors = Boolean(
    titleError ||
    descriptionError ||
    categoryNameError ||
    categoryDescriptionError ||
    countryError ||
    hasQuestionErrors,
  );

  const formPublishTranslationIssues =
    isSpeakUpForm &&
    (action === FORM_ACTIONS.PUBLISH || action === FORM_ACTIONS.DRAFT) &&
    selectedLanguages.some((l) => l.value !== DEFAULT_FORM_EDITING_LOCALE)
      ? collectFormPublishTranslationIssues(
          trimmedQuestions,
          selectedLanguages,
          !isSpeakUpForm,
        )
      : [];

  const hasFormPublishTranslationErrors =
    formPublishTranslationIssues.length > 0;

  const hasAnyErrors = hasErrors || hasFormPublishTranslationErrors;

  let trimmedQuestionsOut = trimmedQuestions;
  if (hasQuestionErrors) {
    trimmedQuestionsOut = markQuestionErrors(
      trimmedQuestionsOut,
      !isSpeakUpForm,
    );
  }
  if (hasFormPublishTranslationErrors) {
    trimmedQuestionsOut = applyFormPublishTranslationIssuesToQuestions(
      trimmedQuestionsOut,
      formPublishTranslationIssues,
    );
  }
  if (hasQuestionErrors || hasFormPublishTranslationErrors) {
    setQuestions(trimmedQuestionsOut);
  }

  if (hasAnyErrors) {
    if (action !== FORM_ACTIONS.PREVIEW) {
      if (!titleTouched) setTitleTouched(true);
      if (!descriptionTouched) setDescriptionTouched(true);
      if (!categoryNameTouched) setCategoryNameTouched(true);
      if (!categoryDescriptionTouched) setCategoryDescriptionTouched(true);
      if (!isSpeakUpForm && !countryTouched) setCountryTouched(true);
    }
  }

  return {
    isValid: !hasAnyErrors,
    trimmedTitle,
    trimmedDescription,
    trimmedCategoryName,
    trimmedCategoryDescription,
    trimmedQuestions: trimmedQuestionsOut,
    titleError,
    descriptionError,
    categoryNameError,
    categoryDescriptionError,
    countryError,
    hasQuestionErrors,
    questionsWithInvalidText,
    questionsWithInvalidDescription,
    questionsWithoutAnswerType,
    questionsWithEmptyOptions,
    questionsWithInvalidOptions,
    questionsWithMissingDatabaseField,
    formPublishTranslationIssues,
    hasFormPublishTranslationErrors,
  };
}
